import { Link } from "react-router-dom";
import { ArrowRight, FileSearch, Layers, ScrollText, CheckCircle2 } from "lucide-react";

const PROOF = [
  ["Specifications", "Division 05 and everything that references steel"],
  ["Contracts", "Payment, LDs, bonding, flow-downs, notice"],
  ["Addenda", "What changed, what it supersedes, what it costs"],
  ["Drawings & attachments", "Sheets, sketches, scanned pages — read, not skipped"],
];

export function Hero() {
  return (
    <section className="relative overflow-hidden" data-testid="home-hero">
      <div className="absolute inset-0 grid-bg opacity-70 pointer-events-none" />
      <div className="absolute -top-40 right-[-10%] w-[560px] h-[560px] rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 sm:px-8 pt-16 sm:pt-24 pb-16 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <div className="label-cap text-amber-500">// SPEC, CONTRACT &amp; ADDENDUM REVIEW FOR STRUCTURAL STEEL FABRICATORS</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-100 mt-4 leading-[1.05]">
            Find what the steel package <span className="text-amber-400">actually requires</span> — before you price it.
          </h1>
          <p className="font-heading text-lg md:text-xl font-semibold text-slate-100 mt-6 max-w-2xl leading-snug" data-testid="hero-philosophy">
            AI shouldn't make the bid decision. <span className="text-slate-300">It should make sure you see the decisions that need to be made.</span>
          </p>
          <p className="text-base md:text-lg text-slate-400 mt-4 max-w-2xl leading-relaxed" data-testid="hero-description">
            SteelForgeAI reviews the specifications, contracts, addenda and drawings behind your bid against the way your company actually bids work — surfacing scope conflicts, addendum impacts, contract risk, RFIs, clarifications and the decisions that need an estimator's attention, with the page and clause to prove it.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/register" className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold px-6 py-3 rounded-md text-base transition-colors" data-testid="hero-cta-primary">
              Review Your First Project <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/pricing" className="inline-flex items-center justify-center gap-2 border border-slate-700 hover:border-slate-500 text-slate-200 px-6 py-3 rounded-md text-base transition-colors" data-testid="hero-cta-pricing">
              See pricing
            </Link>
          </div>
          <div className="mt-4 text-xs text-slate-500">14-day trial · no credit card · your documents stay in your workspace</div>
        </div>
        <div className="lg:col-span-5">
          <div className="sc-card p-5 border-slate-700/70 shadow-2xl shadow-black/40" data-testid="hero-proof-card">
            <div className="flex items-center justify-between">
              <div className="label-cap text-slate-400">What one review covers</div>
              <div className="font-mono text-[10px] text-emerald-400 border border-emerald-800 rounded px-1.5 py-0.5">SOURCE-CITED</div>
            </div>
            <ul className="mt-4 divide-y divide-slate-800">
              {PROOF.map(([t, d]) => (
                <li key={t} className="py-3 flex gap-3">
                  <CheckCircle2 className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                  <div><div className="text-sm font-semibold text-slate-100">{t}</div><div className="text-xs text-slate-400">{d}</div></div>
                </li>
              ))}
            </ul>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              {[[FileSearch, "Findings"], [ScrollText, "Draft RFIs"], [Layers, "PM Report"]].map(([I, l]) => (
                <div key={l} className="bg-slate-950/60 border border-slate-800 rounded-md py-2"><I className="w-4 h-4 mx-auto text-slate-300" /><div className="text-[11px] text-slate-400 mt-1">{l}</div></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
